import { useEffect, useState } from 'react';
import { Trophy, CaretRight } from '@phosphor-icons/react';
import api from '../lib/api';
import { ACCENT_DOT } from '../lib/partners';

const money = (n) => `$${(n || 0).toFixed(2)}`;

const Stat = ({ label, value, testid, strong }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">{label}</p>
    <p className={`text-xl font-black leading-tight mt-0.5 ${strong ? 'text-emerald-700' : 'text-gray-900'}`}>{value}</p>
  </div>
);

const RankBadge = ({ rank }) => {
  const tone = rank === 1 ? 'bg-amber-400 text-white' : rank === 2 ? 'bg-gray-300 text-gray-800' : rank === 3 ? 'bg-orange-300 text-orange-900' : 'bg-gray-100 text-gray-500';
  return <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-black ${tone}`}>{rank}</span>;
};

export default function PartnerLeaderboard({ onSelect, tx }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/partners/leaderboard').then((r) => setData(r.data)).catch(() => setData({ partners: [], totals: {} }));
  }, []);

  if (!data) {
    return <div className="kpi-card py-10 text-center text-gray-400" data-testid="leaderboard-loading">{tx('Loading…', 'Cargando…', 'Chargement…')}</div>;
  }
  const { partners, totals } = data;
  const maxKg = Math.max(1, ...partners.map((p) => p.invoiced_kg));

  return (
    <div className="space-y-3" data-testid="partner-leaderboard">
      <div className="kpi-card grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="leaderboard-totals">
        <Stat label={tx('Partners', 'Socios', 'Partenaires')} value={partners.length} testid="leaderboard-total-partners" />
        <Stat label={tx('Clients', 'Clientes', 'Clients')} value={totals.clients || 0} testid="leaderboard-total-clients" />
        <Stat label={tx('Invoiced kg', 'Kg facturados', 'Kg facturés')} value={(totals.invoiced_kg || 0).toFixed(2)} testid="leaderboard-total-kg" />
        <Stat label={tx('Earned', 'Ganado', 'Gagné')} value={money(totals.total_earned)} testid="leaderboard-total-earned" />
        <Stat label={tx('Paid', 'Pagado', 'Payé')} value={money(totals.total_paid)} testid="leaderboard-total-paid" />
        <Stat label={tx('Owed', 'Pendiente', 'Dû')} value={money(totals.total_owed)} testid="leaderboard-total-owed" strong />
      </div>

      <div className="kpi-card p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaderboard-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
              <th className="px-4 py-3"><Trophy size={16} className="text-amber-500" weight="duotone" /></th>
              <th className="px-2 py-3">{tx('Partner', 'Socio', 'Partenaire')}</th>
              <th className="px-2 py-3 text-right">{tx('Clients', 'Clientes', 'Clients')}</th>
              <th className="px-2 py-3 text-right">{tx('Packages', 'Paquetes', 'Colis')}</th>
              <th className="px-2 py-3 min-w-[160px]">{tx('Invoiced kg', 'Kg facturados', 'Kg facturés')}</th>
              <th className="px-2 py-3 text-right">{tx('Rate', 'Tarifa', 'Taux')}</th>
              <th className="px-2 py-3 text-right">{tx('Earned', 'Ganado', 'Gagné')}</th>
              <th className="px-2 py-3 text-right">{tx('Paid', 'Pagado', 'Payé')}</th>
              <th className="px-2 py-3 text-right">{tx('Owed', 'Pendiente', 'Dû')}</th>
              <th className="px-2 py-3" />
            </tr>
          </thead>
          <tbody>
            {partners.map((p, i) => (
              <tr key={p.tag} onClick={() => onSelect(p.tag)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`leaderboard-row-${p.tag}`}>
                <td className="px-4 py-3"><RankBadge rank={i + 1} /></td>
                <td className="px-2 py-3 font-semibold text-gray-900 whitespace-nowrap">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${ACCENT_DOT[p.accent] || ACCENT_DOT.amber}`} />
                  {p.name}
                  {!p.active && <span className="ml-2 text-[10px] uppercase font-bold text-gray-400">{tx('Disabled', 'Desactivado', 'Désactivé')}</span>}
                </td>
                <td className="px-2 py-3 text-right font-semibold" data-testid={`leaderboard-clients-${p.tag}`}>{p.clients}</td>
                <td className="px-2 py-3 text-right">{p.total_packages}</td>
                <td className="px-2 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className={`h-full rounded-full ${ACCENT_DOT[p.accent] || ACCENT_DOT.amber}`} style={{ width: `${(p.invoiced_kg / maxKg) * 100}%` }} />
                    </div>
                    <span className="text-xs font-mono text-gray-700 w-14 text-right" data-testid={`leaderboard-kg-${p.tag}`}>{p.invoiced_kg.toFixed(2)}</span>
                  </div>
                </td>
                <td className="px-2 py-3 text-right text-gray-500">${p.rate_usd_per_kg.toFixed(2)}/kg</td>
                <td className="px-2 py-3 text-right">{money(p.total_earned)}</td>
                <td className="px-2 py-3 text-right text-gray-500">{money(p.total_paid)}</td>
                <td className="px-2 py-3 text-right font-black text-emerald-700" data-testid={`leaderboard-owed-${p.tag}`}>{money(p.total_owed)}</td>
                <td className="px-2 py-3 text-gray-300"><CaretRight size={14} /></td>
              </tr>
            ))}
            {partners.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">{tx('No partners yet — add one in Settings → Partners.', 'Aún no hay socios.', 'Aucun partenaire.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
